import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from "react";
import { subWeeks, isAfter, parseISO } from 'date-fns';
import { AccountFinal } from "@/api/entities";
import { PersonFinal } from "@/api/entities";
import { MomentumScore } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Import page components
import MomentumTrend from "../components/market/MomentumTrend";
import DecisionWindowRadarFull from "../components/market/DecisionWindowRadarFull";
import LiveDeals from "../components/market/LiveDeals";
import TopAccounts from "../components/market/TopAccounts";
import TopPersonas from "../components/market/TopPersonas";
import MarketFitGrid from "../components/market/MarketFitGrid";
import MarketShare from "../components/market/MarketShare";
import TieringSnapshot from "../components/market/TieringSnapshot";
import AIReadinessIndex from "../components/market/AIReadinessIndex";
import TopBuyers from "../components/market/TopBuyers";
import KeyBuyingAreas from "../components/market/KeyBuyingAreas";
import ProcurementComplexity from "../components/market/ProcurementComplexity";
import ProcurementRoutes from "../components/market/ProcurementRoutes";
import MarketWinners from "../components/market/MarketWinners";
import CompetitorsTab from "../components/market/CompetitorsTab";
import MarketExperience from "../components/market/experience/MarketExperience";
import MarketPlayers from "../components/market/experience/MarketPlayers";
import MarketSectorTrends from "../components/market/experience/MarketSectorTrends";
import MarketRenewalRisk from "../components/market/experience/MarketRenewalRisk";
import MarketIgnore from "../components/market/experience/MarketIgnore";
import MarketFunding from "../components/market/experience/MarketFunding";
import { translate, useTranslation } from '@/components/utils/translations';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { probeCompetitorModule } from '@/lib/competitorsApi';
import { probeMarketModule, fetchMarketLenses, addMarketAccount } from '@/lib/marketExperienceApi';
import { isPilotCapReachedError, pilotErrorMessage } from '@/lib/pilotMode';
import { lensDisplayName, formatCategoryLabel } from '@/lib/marketExperience';
import { useMarketUserFilters } from '@/hooks/useMarketUserFilters';
import { useSearchParams } from 'react-router-dom';

const MARKET_CACHE_KEY = 'market-cache-v5'; // v5: Live Deals uses /api/procurement/query?table=live, N/A deadlines included
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DEFAULT_TENANT = 'instructure';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

const COUNTRY_TO_MARKET = {
  'hong kong': 'hong-kong',
  'australia': 'australia',
  'south africa': 'south-africa',
  'united kingdom': 'united-kingdom',
};

function defaultMarketForTenant(tenantId) {
  const id = (tenantId || '').toLowerCase();
  if (id === 'clairelabs') return 'hong-kong';
  if (id === 'smowl') return 'australia';
  if (id === 'diambra') return 'united-kingdom'; // Same UK data as smowl
  if (id === 'faria' || id === 'edqwest') return 'edqwest';
  if (id === 'demarque' || id === 'demarque_v2') return 'demarque';
  return 'australia'; // Default for instructure, etc.
}

function readMarketCache(tenantId) {
  try {
    const raw = sessionStorage.getItem(`${MARKET_CACHE_KEY}:${tenantId}`);
    if (!raw) return null;

    const cached = JSON.parse(raw);
    if (Date.now() - cached.cachedAt > CACHE_DURATION) {
      sessionStorage.removeItem(`${MARKET_CACHE_KEY}:${tenantId}`); // Cache expired
      return null;
    }
    return cached;
  } catch { return null; }
}

function writeMarketCache(tenantId, snapshot) {
  try {
    sessionStorage.setItem(`${MARKET_CACHE_KEY}:${tenantId}`, JSON.stringify({ ...snapshot, cachedAt: Date.now() }));
  } catch {}
}

const RATE_LIMIT_COOLDOWN_KEY = 'rate-limit-cooldown-market';
const RATE_LIMIT_COOLDOWN_DURATION = 20 * 60 * 1000; // 20 minutes
const RATE_LIMIT_TOAST_SHOWN_KEY = 'rate-limit-toast-shown-market';

function isInRateLimitCooldown() {
  try {
    const cooldownEnd = localStorage.getItem(RATE_LIMIT_COOLDOWN_KEY);
    if (!cooldownEnd) return false;
    return Date.now() < parseInt(cooldownEnd);
  } catch {
    return false;
  }
}

function getRemainingCooldownTime() {
  try {
    const cooldownEnd = localStorage.getItem(RATE_LIMIT_COOLDOWN_KEY);
    if (!cooldownEnd) return 0;
    return Math.max(0, parseInt(cooldownEnd) - Date.now());
  } catch {
    return 0;
  }
}

function formatRemainingTime(ms) {
  const minutes = Math.ceil(ms / (1000 * 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

function setRateLimitCooldown() {
  try {
    localStorage.setItem(RATE_LIMIT_COOLDOWN_KEY, String(Date.now() + RATE_LIMIT_COOLDOWN_DURATION));
  } catch {}
}

function clearRateLimitCooldown() {
  try {
    localStorage.removeItem(RATE_LIMIT_COOLDOWN_KEY);
    localStorage.removeItem(RATE_LIMIT_TOAST_SHOWN_KEY); // Clear toast shown state as well
  } catch {}
}

function shouldShowRateLimitToast() {
  try {
    const lastShown = localStorage.getItem(RATE_LIMIT_TOAST_SHOWN_KEY);
    // Show toast only once per cooldown period
    return !lastShown || (Date.now() - parseInt(lastShown) > RATE_LIMIT_COOLDOWN_DURATION);
  } catch {
    return true; // If error reading, default to showing
  }
}

function markRateLimitToastShown() {
  try {
    localStorage.setItem(RATE_LIMIT_TOAST_SHOWN_KEY, String(Date.now()));
  } catch {}
}

// Fetch live deals from same API as Live Radar (Procurement page) - /api/procurement/query table=live
async function fetchLiveDealsFromProcurementApi(tenantId, limit = 500) {
  const allItems = [];
  let currentCursor = null;
  let page = 0;
  const maxPages = 10;
  do {
    page++;
    if (page > maxPages) break;
    const url = currentCursor
      ? `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`
      : `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true`;
    const res = await fetch(url, { headers: { 'x-tenant-id': tenantId }, credentials: 'include' });
    const pageData = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn('[Market] Live deals fetch failed:', pageData?.error || res.status);
      break;
    }
    const items = pageData.data || [];
    allItems.push(...items);
    currentCursor = pageData.nextCursor;
  } while (currentCursor);
  return allItems;
}

// Basic fetch with retry implementation
async function fetchWithRetry(fetcher, retries = 3, delay = 1000) {
  for (let i = 0; i < retries; i++) {
    try {
      return await fetcher();
    } catch (error) {
      if (error.response?.status === 429 || error.status === 429) {
        throw error; // Immediately re-throw rate limit errors
      }
      if (i < retries - 1) {
        console.warn(`Fetch attempt ${i + 1} failed, retrying in ${delay}ms:`, error);
        await new Promise(res => setTimeout(res, delay));
      } else {
        throw error;
      }
    }
  }
  return null; // Should not be reached
}

// Fetch account tiers from our API
async function fetchAccountTiers(tenantId) {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/analytics-v2/accounts/comms-depth?tenant_id=${tenantId}&days=9999`,
      { credentials: 'include' }
    );
    if (response.ok) {
      const data = await response.json();
      const tierMap = {};
      data.forEach(acc => {
        // Store by both account_id and account_name for flexible matching
        if (acc.account_id) tierMap[acc.account_id] = acc.account_tier;
        if (acc.account_name) tierMap[acc.account_name] = acc.account_tier;
      });
      console.log('📊 Fetched account tiers:', Object.keys(tierMap).length, 'accounts');
      return tierMap;
    }
  } catch (e) {
    console.error('Error fetching account tiers:', e);
  }
  return {};
}

const applyTenantOverrides = (entity, currentTenantId) => {
  if (!entity || !entity.tenant_overrides || !currentTenantId) {
    return entity;
  }
  const override = entity.tenant_overrides[currentTenantId];
  if (override) {
    return { ...entity, ...override };
  }
  return entity;
};

export default function Market({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [accounts, setAccounts] = useState([]);
  const [allAccountsIncludingHidden, setAllAccountsIncludingHidden] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [procurement, setProcurement] = useState([]);
  const [momentumScores, setMomentumScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);
  const [filters, setFilters] = useState({
    country: "all",
  });
  const [activeTab, setActiveTab] = useState("overview");
  const [competitorModuleStatus, setCompetitorModuleStatus] = useState('pending');
  const [marketModuleStatus, setMarketModuleStatus] = useState('pending');
  const [marketEntitledCountries, setMarketEntitledCountries] = useState([]);
  const [marketLenses, setMarketLenses] = useState([]);
  const [marketLens, setMarketLens] = useState(null);
  /* [2026-10-07] Which category of the active lens the whole Market page is showing.
   * 'all' is the default and is byte-for-byte the previous behaviour. */
  const [marketCategory, setMarketCategory] = useState('all');
  /* The lens owns its category set, so the options come from the lenses payload rather than a
   * constant here — a hard-coded list would drift the moment a tenant gains or loses a category. */
  const activeLensCategories = useMemo(() => {
    const l = (marketLenses || []).find((x) => x.lens_key === marketLens);
    return Array.isArray(l?.categories) ? l.categories : [];
  }, [marketLenses, marketLens]);
  /* A category only exists inside the lens that carries it. Without this, switching lens would ask
   * for a foreign category and the page would render an empty market that reads as a data fault. */
  useEffect(() => { setMarketCategory('all'); }, [marketLens]);
  const [canEditMarket, setCanEditMarket] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const aliveRef = useRef(true);
  const loadAttemptedRef = useRef(false); // To prevent multiple simultaneous data loads
  const dataLoadedRef = useRef(false); // To track if data has been successfully loaded
  const lastLoadedTenantRef = useRef(null); // To track the tenant for which data was last loaded
  const { toast } = useToast();

  const effectiveTenant = useMemo(() => {
    return resolveEffectiveTenant(
      contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
      currentTenant,
      contextUser,
      DEFAULT_TENANT
    );
  }, [currentTenant, contextUser?.tenant_id, contextUser]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'competitors' && competitorModuleStatus === 'ready') {
      setActiveTab('competitors');
    }
  }, [searchParams, competitorModuleStatus]);

  /* ?add=<account_id> — a link that ADDS, rather than one that drops the rep here to find the row
   * themselves.
   *
   * Tiago: "the link to add an account goes to Market... instead it should just add the account
   * upon click (we already set this up in the platform)." It is set up for a CHIP — NeoChatPanel
   * calls addMarketAccount directly — but a chip only exists where our own UI renders the answer.
   * Claude in a chat, or on a phone, can offer a URL and nothing else, and that URL used to land
   * on an unfiltered market list with no indication of which row was meant.
   *
   * 🔴 ONCE PER ID, AND THE PARAM IS CLEARED EITHER WAY. A route effect re-runs on re-render, and
   * add is a selection against a capped pilot allowance — so a retry loop here would quietly spend
   * a rep's slots. The ref is keyed on the id, and the param is stripped before the request is
   * even sent, so a reload cannot repeat it.
   *
   * ⚠️ ALREADY-ADDED IS A SUCCESS, NOT AN ERROR. The endpoint returns the account in `skipped`,
   * and a rep clicking a link for an account they already hold should be told it is there, not
   * shown a failure. The pilot cap is the one case that needs its own words: the BE owns that
   * message (it names the cap), so it is passed through rather than reworded. */
  const addParamHandledRef = useRef(null);
  useEffect(() => {
    const wanted = String(searchParams.get('add') || '').trim();
    if (!wanted || !effectiveTenant) return;
    if (addParamHandledRef.current === wanted) return;
    addParamHandledRef.current = wanted;

    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        const result = await addMarketAccount(effectiveTenant, { account_id: wanted });
        const added = Array.isArray(result?.added) ? result.added.length : 0;
        const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
        if (added > 0) {
          // translate() rather than the hook's t: keeps this effect's dependency list unchanged.
          toast({
            title: translate('mktAddedToYourAccounts'),
            description: translate('mktAddedLoadingNow'),
            duration: 6000,
          });
        } else if (skipped > 0) {
          toast({
            title: translate('mktAlreadyOnYourList'),
            description: translate('mktNothingChangedAlreadyThere'),
            duration: 5000,
          });
        } else {
          toast({
            title: translate('mktCouldNotAddThisAccount'),
            description: pilotErrorMessage(result, translate('mktNeoDidNotSayWhy')),
            variant: 'destructive',
            duration: 8000,
          });
        }
      } catch (e) {
        toast({
          title: isPilotCapReachedError(e) ? translate('mktYourPilotIsFull') : translate('mktCouldNotAddThisAccount'),
          description: pilotErrorMessage(e, translate('pleaseTryAgain')),
          variant: 'destructive',
          duration: 9000,
        });
      }
    })();
  }, [searchParams, setSearchParams, effectiveTenant, toast]);

  useEffect(() => {
    let cancelled = false;
    if (!effectiveTenant) return;
    setMarketModuleStatus('pending');
    probeCompetitorModule(effectiveTenant)
      .then((status) => {
        if (!cancelled) setCompetitorModuleStatus(status);
      })
      .catch(() => {
        if (!cancelled) setCompetitorModuleStatus('disabled');
      });
    probeMarketModule(effectiveTenant)
      .then((status) => {
        if (!cancelled) setMarketModuleStatus(status);
      })
      .catch(() => {
        if (!cancelled) setMarketModuleStatus('fault');
      });
    return () => { cancelled = true; };
  }, [effectiveTenant]);

  const showCompetitorsTab = competitorModuleStatus === 'ready';
  const marketModulePending = marketModuleStatus === 'pending';
  const showMarketExperience = marketModuleStatus === 'on' || marketModuleStatus === 'needs_setup' || marketModuleStatus === 'fault';
  const showBuyingActivityTab = marketModuleStatus === 'off';
  const showLegacyMarket = marketModuleStatus === 'off';
  const userFilters = useMarketUserFilters(effectiveTenant, { enabled: showMarketExperience });
  const countryFilter = showMarketExperience ? userFilters.country : filters.country;
  const marketTabCount = [
    true,
    showBuyingActivityTab,
    true,
    showCompetitorsTab,
    showMarketExperience,
    showMarketExperience,
    showMarketExperience,
  ].filter(Boolean).length;

  useEffect(() => {
    if (!showMarketExperience || !effectiveTenant) {
      setMarketEntitledCountries([]);
      setMarketLenses([]);
      setMarketLens(null);
      setCanEditMarket(false);
      return;
    }
    let cancelled = false;
    fetchMarketLenses(effectiveTenant)
      .then((data) => {
        if (cancelled) return;
        const list = data?.lenses || [];
        setMarketLenses(list);
        setCanEditMarket(Boolean(data?.can_edit));
        const fallback = data?.default_lens || list[0]?.lens_key || null;
        setMarketLens((prev) => (prev && list.some((l) => l.lens_key === prev) ? prev : fallback));
      })
      .catch(() => {
        if (!cancelled) {
          setMarketLenses([]);
          setMarketLens(null);
          setCanEditMarket(false);
        }
      });
    return () => { cancelled = true; };
  }, [showMarketExperience, effectiveTenant]);

  useEffect(() => {
    const selected = marketLenses.find((l) => l.lens_key === marketLens) || marketLenses[0];
    setMarketEntitledCountries(selected?.countries || []);
  }, [marketLenses, marketLens]);

  useEffect(() => {
    if (!showMarketExperience || !userFilters.ready) return;
    if (userFilters.country === 'all') return;
    const allowedList = (userFilters.countryOptions.length
      ? userFilters.countryOptions
      : marketEntitledCountries);
    if (!allowedList.length) return;
    const allowed = allowedList.some(
      (c) => c.toLowerCase() === String(userFilters.country).toLowerCase(),
    );
    if (!allowed) userFilters.setCountry('all');
  }, [showMarketExperience, userFilters.ready, userFilters.country, userFilters.countryOptions, marketEntitledCountries, userFilters.setCountry]);

  useEffect(() => {
    if (!showBuyingActivityTab && activeTab === 'buying') {
      setActiveTab('overview');
    }
  }, [showBuyingActivityTab, activeTab]);

  // Re-hydrate from cache whenever tenant context changes
  useEffect(() => {
    const tenantId = (effectiveTenant || DEFAULT_TENANT).toLowerCase();
    const cached = readMarketCache(tenantId);
    if (cached) {
      // Build tenantMatches function to filter cached data
      const tenantMatches = (record) => {
        if (!record) return false;
        if (tenantId === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
        if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
        if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
        if (tenantId === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
        if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (tenantId === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
        if (tenantId === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
        if (tenantId === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
        if (tenantId === 'kairadigital') return record.tenant_id === 'kairadigital';
        if (tenantId === 'plataformaa') return record.tenant_id === 'plataformaa';
        if (tenantId === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
        return false;
      };

      // Filter cached data to ensure tenant isolation
      const filteredAccounts = (cached.accounts || []).filter(acc => tenantMatches(acc) && acc.is_visible !== false);
      const filteredPersonas = (cached.personas || []).filter(p => tenantMatches(p) && p.is_visible !== false);
      const filteredProcurement = (cached.procurement || []).filter(p => tenantMatches(p));
      const filteredMomentum = (cached.momentumScores || []).filter(m => tenantMatches(m));

      if (filteredAccounts.length !== (cached.accounts || []).length ||
          filteredPersonas.length !== (cached.personas || []).length) {
        console.warn('⚠️ Market: Filtered out cached data that doesn\'t match tenant:', tenantId, {
          accounts: (cached.accounts || []).length - filteredAccounts.length,
          personas: (cached.personas || []).length - filteredPersonas.length
        });
      }
      
      setAccounts(filteredAccounts);
      setAllAccountsIncludingHidden(filteredAccounts);
      setPersonas(filteredPersonas);
      setProcurement(filteredProcurement);
      setMomentumScores(filteredMomentum);
      setHydratedFromCache(true);
      setIsLoading(false);
      dataLoadedRef.current = true;
      lastLoadedTenantRef.current = tenantId;
    } else {
      setHydratedFromCache(false);
      // Reset data if no cache for new tenant, to avoid showing stale data from previous tenant
      setAccounts([]);
      setAllAccountsIncludingHidden([]);
      setPersonas([]);
      setProcurement([]);
      setMomentumScores([]);
      dataLoadedRef.current = false;
      lastLoadedTenantRef.current = null;
    }
  }, [effectiveTenant]);

  const loadMarketData = useCallback(async () => {
    if (loadAttemptedRef.current) {
      console.log('⏭️ Load already in progress, skipping');
      return;
    }

    if (!contextUser) return;
    const tenantId = resolveEffectiveTenant(
      contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
      currentTenant,
      contextUser,
      DEFAULT_TENANT
    ).toLowerCase();

    if (dataLoadedRef.current && 
        accounts.length > 0 && 
        lastLoadedTenantRef.current === tenantId) {
      console.log('✅ Data already loaded for current tenant, skipping');
      return;
    }

    console.log('🔄 Starting data load for tenant:', tenantId);
    loadAttemptedRef.current = true;

    if (isInRateLimitCooldown()) {
      const cached = readMarketCache(tenantId);
      if (cached) {
        const remaining = getRemainingCooldownTime();
        console.log(`⏸️ In cooldown (${formatRemainingTime(remaining)}), using cached data`);

        // Build tenantMatches function to filter cached data
        const tenantMatches = (record) => {
          if (!record) return false;
          if (tenantId === 'smowl') return record.tenant_id === 'smowl';
          if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
          if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
          if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
          if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
          if (tenantId === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
          if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
          if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
          if (tenantId === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
          if (tenantId === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
          if (tenantId === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
          if (tenantId === 'kairadigital') return record.tenant_id === 'kairadigital';
          if (tenantId === 'plataformaa') return record.tenant_id === 'plataformaa';
          if (tenantId === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
          return false;
        };

        // Filter cached data to ensure tenant isolation
        const filteredAccounts = (cached.accounts || []).filter(acc => tenantMatches(acc) && acc.is_visible !== false);
        const filteredPersonas = (cached.personas || []).filter(p => tenantMatches(p) && p.is_visible !== false);
        const filteredProcurement = (cached.procurement || []).filter(p => tenantMatches(p));
        const filteredMomentum = (cached.momentumScores || []).filter(m => tenantMatches(m));

        if (filteredAccounts.length !== (cached.accounts || []).length ||
            filteredPersonas.length !== (cached.personas || []).length) {
          console.warn('⚠️ Market: Filtered out cached data that doesn\'t match tenant:', tenantId);
        }

        setAccounts(filteredAccounts);
        setAllAccountsIncludingHidden(filteredAccounts);
        setPersonas(filteredPersonas);
        setProcurement(filteredProcurement);
        setMomentumScores(filteredMomentum);
        setHydratedFromCache(true);
        setIsLoading(false);
        dataLoadedRef.current = true;
        lastLoadedTenantRef.current = tenantId;

        if (shouldShowRateLimitToast()) {
          toast({
            title: translate('mktRateLimitActive'),
            description: translate('mktUsingCachedDataRetryIn').replace(/\{time\}/g, formatRemainingTime(remaining)),
            variant: "default",
            duration: 8000
          });
          markRateLimitToastShown();
        }
        loadAttemptedRef.current = false;
        return;
      }
    }

    if (!hydratedFromCache && accounts.length === 0) {
      setIsLoading(true);
    }

    try {
      console.log(`📊 Loading Market for tenant: ${tenantId}`);
      
      // Minimal, rule-based tenant filter (AU/HK/ZA/Edqwest visibility)
      let tenantFilter;
      if (tenantId === 'instructure') {
        tenantFilter = { $in: ['instructure', 'clairelabs', 'default', 'global'] };
        console.log('📊 Market filtering for instructure (AU + HK via clairelabs)');
      } else if (tenantId === 'smowl') {
        tenantFilter = { $in: ['smowl'] };
        console.log('📊 Market filtering for smowl (tenant_id = smowl only)');
      } else if (tenantId === 'diambra') {
        tenantFilter = { $in: ['diambra', 'global'] };
        console.log('📊 Market filtering for diambra (tenant-specific only)');
      } else if (tenantId === 'clairelabs') {
        tenantFilter = { $in: ['clairelabs', 'default', 'global'] };
        console.log('📊 Market filtering for clairelabs (HK only)');
      } else if (tenantId === 'faria' || tenantId === 'edqwest') {
        tenantFilter = { $in: ['faria','edqwest', 'default', 'global'] };
        console.log('📊 Market filtering for edqwest/faria');
      } else if (tenantId === 'calmedica') {
        // UPDATED: Only calmedica-specific data, no global
        tenantFilter = { $in: [tenantId] };
        console.log('📊 Market filtering for calmedica (tenant-specific only, no global)');
      } else if (tenantId === 'sebit') {
        tenantFilter = { $in: [tenantId, 'global'] };
        console.log('📊 Market filtering for sebit');
      } else if (tenantId === 'plataformaa') {
        tenantFilter = { $in: [tenantId, 'global'] };
        console.log('📊 Market filtering for plataformaa');
      } else if (tenantId === 'learnwise') {
        tenantFilter = { $in: [tenantId, 'global'] };
        console.log('📊 Market filtering for learnwise');
      } else {
        tenantFilter = { $in: [tenantId, 'default', 'global'] };
        console.log(`📊 Market filtering for ${tenantId}:`, tenantFilter);
      }
      
      console.log('📊 MARKET Full filter object:', { tenant_id: tenantFilter });

      const [accountData, personaData, procurementData, momentumData, accountTiers] = await Promise.all([
        fetchWithRetry(() => AccountFinal.filter({ tenant_id: tenantFilter }, "-account_fit_score", 500)),
        fetchWithRetry(() => PersonFinal.filter({ tenant_id: tenantFilter }, "-persona_fit_score", 500)),
        fetchWithRetry(() => fetchLiveDealsFromProcurementApi(tenantId, 500)),
        // Let the backend apply entitlement-driven partition scope (incl. global_temp for V2).
        // Pinning tenant_id replaces that with a hand-maintained list and drops V2 signals.
        fetchWithRetry(() => MomentumScore.filter({}, "-date_published", 500)),
        fetchAccountTiers(tenantId)
      ]);

      const accountsWithOverrides = accountData
        .filter(acc => {
          // For calmedica, only show France accounts
          if (tenantId === 'calmedica' && acc.country !== 'France') {
            console.log(`🚫 Filtering out non-France account for calmedica:`, acc.account_name, acc.country);
            return false;
          }
          return true;
        })
        .map(acc => {
          const withOverrides = applyTenantOverrides(acc, tenantId);
          // Merge account_tier from our API if available
          const apiTier = accountTiers[acc.account_id] || accountTiers[acc.account_name];
          if (apiTier) {
            return { ...withOverrides, account_tier: apiTier };
          }
          return withOverrides;
        });
        
      const visibleAccounts = accountsWithOverrides.filter(acc => acc.is_visible !== false);
      const visibleAccountNames = new Set(visibleAccounts.map(a => a.account_name));
      
      const personasWithOverrides = personaData
        .map(p => applyTenantOverrides(p, tenantId));
        
      const visiblePersonas = personasWithOverrides.filter(p => {
        if (p.is_visible === false) return false;
        if (!p.account_name) return false;
        return visibleAccountNames.has(p.account_name);
      });

      // Fetch pursuit status from API (user-specific)
let personasWithPursuit = visiblePersonas;
try {
  const pursuitRes = await fetch(
    `${API_BASE_URL}/api/diy-playbooks/personas-in-pursuit?tenant_id=${tenantId}&user_id=${contextUser?.id}`,
    { credentials: 'include' }
  );
  if (pursuitRes.ok) {
    const pursuitData = await pursuitRes.json();
    const pursuitArray = pursuitData.personas || pursuitData || [];
    const pursuitIds = new Set(pursuitArray.map(p => p.person_id));
    
    personasWithPursuit = visiblePersonas.map(p => ({
      ...p,
      add_to_pursuit: pursuitIds.has(p.person_id) || pursuitIds.has(p.id)
    }));
    
    console.log('✅ Market: Merged pursuit status:', pursuitIds.size, 'personas in pursuit');
  }
} catch (e) {
  console.warn('Could not load pursuit status for Market:', e);
}

      // FIXED: Filter momentum scores to only match visible accounts and enrich with country
      const accountsByNameMap = new Map(visibleAccounts.map(acc => [acc.account_name, acc]));
      let filteredMomentumScores = momentumData
        .filter(m => {
          // Match momentum to visible accounts only
          const matchesVisibleAccount = visibleAccountNames.has(m.institution_name);
          return matchesVisibleAccount;
        })
        .map(m => {
          // Enrich momentum score with country from matching account (always override if account has country)
          const matchingAccount = accountsByNameMap.get(m.institution_name);
          if (matchingAccount && matchingAccount.country) {
            return { ...m, country: matchingAccount.country };
          }
          return m;
        });

      // Diambra: Market Momentum chart shows United Kingdom data only
      if (tenantId === 'diambra') {
        const before = filteredMomentumScores.length;
        filteredMomentumScores = filteredMomentumScores.filter(m => m.country === 'United Kingdom');
        console.log('📊 Market: Diambra — momentum chart UK-only:', filteredMomentumScores.length, 'of', before);
      }

      console.log('📊 Market page filtering:', {
        accounts: `${visibleAccounts.length}/${accountData.length}`,
        personas: `${visiblePersonas.length}/${personaData.length}`,
        momentum: `${filteredMomentumScores.length}/${momentumData.length}`,
        allAccountsForMarketShare: accountsWithOverrides.length,
        franceOnlyForCalmedica: tenantId === 'calmedica' ?
          accountData.filter(a => a.country !== 'France').length
          : 0
      });

      console.log(`✅ Market RAW momentum data:`, {
        total: momentumData.length,
        afterFiltering: filteredMomentumScores.length,
        sample: filteredMomentumScores.slice(0, 3).map(m => ({
          institution: m.institution_name,
          tenant_id: m.tenant_id,
          period: m.weekly_period,
          change: m.momentum_score_change
        }))
      });

      console.log(`✅ Market data loaded for tenant: ${tenantId}`, {
        accounts: visibleAccounts.length,
        allAccounts: accountsWithOverrides.length,
        people: visiblePersonas.length,
        procurement: procurementData.length,
        momentum: filteredMomentumScores.length,
        momentumSample: filteredMomentumScores.slice(0, 3).map(m => ({
          institution: m.institution_name,
          tenant_id: m.tenant_id,
          weekly_period: m.weekly_period,
          change: m.momentum_score_change
        })),
        tenantBreakdown: {
          accounts: visibleAccounts.reduce((acc, a) => { acc[a.tenant_id || 'null'] = (acc[a.tenant_id || 'null'] || 0) + 1; return acc; }, {}),
          momentum: filteredMomentumScores.reduce((acc, m) => { acc[m.tenant_id || 'null'] = (acc[m.tenant_id || 'null'] || 0) + 1; return acc; }, {})
        }
      });

      clearRateLimitCooldown();

      if (!aliveRef.current) return;

      startTransition(() => {
        if (!aliveRef.current) return;
        setAccounts(visibleAccounts);
        setAllAccountsIncludingHidden(accountsWithOverrides);
        setPersonas(personasWithPursuit);
        setProcurement(procurementData);
        setMomentumScores(filteredMomentumScores);

        writeMarketCache(tenantId, {
          accounts: visibleAccounts,
          allAccountsIncludingHidden: accountsWithOverrides,
          personas: personasWithPursuit,
          procurement: procurementData,
          momentumScores: filteredMomentumScores
        });

        setIsLoading(false);
        setHydratedFromCache(true);
        dataLoadedRef.current = true;
        lastLoadedTenantRef.current = tenantId;
      });

    } catch (error) {
      if (!aliveRef.current) return;
      console.error('❌ Error loading market data:', error);

      const isRateLimit = error.response?.status === 429 || error.status === 429 || error.message?.includes('429');

      if (isRateLimit) {
        console.log('🚫 Rate limit detected, setting cooldown');
        setRateLimitCooldown();

        const cached = readMarketCache((effectiveTenant || DEFAULT_TENANT).toLowerCase());
        if (cached) {
          console.log('📦 Using cached data due to rate limit');
          setAccounts(cached.accounts || []);
          setAllAccountsIncludingHidden(cached.allAccountsIncludingHidden || cached.accounts || []);
          setPersonas(cached.personas || []);
          setProcurement(cached.procurement || []);
          setMomentumScores(cached.momentumScores || []);
          setHydratedFromCache(true);
          dataLoadedRef.current = true;
          lastLoadedTenantRef.current = tenantId;

          if (shouldShowRateLimitToast()) {
            toast({
              title: translate('mktRateLimitReached'),
              description: translate('mktShowingCachedDataRetry20'),
              variant: "default",
              duration: 10000
            });
            markRateLimitToastShown();
          }
        } else {
          if (shouldShowRateLimitToast()) {
            toast({
              title: translate('mktRateLimitReached'),
              description: translate('mktNoCachedDataWait20'),
              variant: "destructive",
              duration: 15000
            });
            markRateLimitToastShown();
          }
        }
      } else {
        toast({ title: translate('error'), description: translate('mktCouldNotLoadMarketData'), variant: "destructive" });
      }

      setIsLoading(false);
    } finally {
      loadAttemptedRef.current = false;
    }
  }, [toast, currentTenant, accounts.length, hydratedFromCache, contextUser?.tenant_id, contextUser, effectiveTenant]);

  // The legacy load (accounts, personas, live deals, momentum, tiers, pursuit — ~6 MB for
  // constructor_tech) feeds ONLY the legacy layout. With the Market module on it was downloaded
  // and never rendered, competing with /market/summary for the same backend. Run it only when
  // the module is off; while the probe is pending, wait.
  useEffect(() => {
    aliveRef.current = true;
    if (marketModuleStatus === 'off') loadMarketData();
    return () => { aliveRef.current = false; };
  }, [loadMarketData, marketModuleStatus]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredAccounts = useMemo(() => {
    const now = new Date();
    const twelveWeeksAgo = subWeeks(now, 12);
    
    const accountsWithMomentum = accounts.map(acc => {
      const accountMomentum = momentumScores.filter(m => {
        if (!m.weekly_period) return false;
        
        const institutionMatch = m.institution_name === acc.account_name;
        
        let dateValid = false;
        try {
          const periodDate = parseISO(m.weekly_period);
          dateValid = !isNaN(periodDate.getTime()) && isAfter(periodDate, twelveWeeksAgo);
        } catch (e) {
          console.warn('Date parse error for momentum:', m.weekly_period, e);
        }
        
        return institutionMatch && dateValid;
      });
      
      const momentum_12w = accountMomentum.reduce((sum, m) => sum + (m.momentum_score_change || 0), 0);
      
      return {
        ...acc,
        momentum_12w
      };
    });

    const filtered = accountsWithMomentum.filter(acc => {
      if (filters.country === "all") {
        return true;
      }
      
      if (acc.country && acc.country.toLowerCase() === filters.country.toLowerCase()) {
        return true;
      }
      
      return false;
    });
    
    return filtered;
  }, [accounts, momentumScores, filters.country]);
  
  const filteredAccountNames = useMemo(() => {
    return new Set(filteredAccounts.map(a => a.account_name));
  }, [filteredAccounts]);
  
  const filteredPersonas = useMemo(() => {
    const result = personas.filter(p => {
      if (p.account_name && filteredAccountNames.has(p.account_name)) {
        return true;
      }
      
      return false;
    });
    
    console.log('📊 Market filtered personas:', {
      total: personas.length,
      afterFilter: result.length,
      countryFilter: filters.country
    });
    
    return result;
  }, [personas, filteredAccountNames]);
  
  const filteredProcurement = useMemo(() => {
    const liveDeals = procurement.filter(p => p.status === 'Live');
    
    const filtered = liveDeals.filter(p => {
      return filters.country === "all" || p.country === filters.country;
    });
    
    console.log('🔍 LiveDeals filteredProcurement:', {
      totalProcurement: procurement.length,
      liveDeals: liveDeals.length,
      afterCountryFilter: filtered.length,
      countryFilter: filters.country,
      sample: filtered.slice(0, 3).map(p => ({
        title: p.title,
        status: p.status,
        tenant_id: p.tenant_id,
        country: p.country
      }))
    });
    
    return filtered;
  }, [procurement, filters.country]);

  const accountsForMarketShare = useMemo(() => {
    
    if (effectiveTenant === 'instructure' || effectiveTenant === 'smowl' || effectiveTenant === 'diambra') {
      console.log('📊 Market Share: Including ALL accounts (even hidden) for', effectiveTenant, {
        totalAccounts: allAccountsIncludingHidden.length,
        countryFilter: filters.country
      });
      
      if (filters.country === "all") {
        return allAccountsIncludingHidden;
      }
      return allAccountsIncludingHidden.filter(acc => 
        acc.country && acc.country.toLowerCase() === filters.country.toLowerCase()
      );
    }
    
    return filteredAccounts;
  }, [allAccountsIncludingHidden, filteredAccounts, filters.country, effectiveTenant]);

  const availableCountries = useMemo(() => {
    if (showMarketExperience) {
      const fromPrefs = userFilters.countryOptions;
      if (fromPrefs.length) return [...fromPrefs].sort();
      if (marketEntitledCountries.length) return [...marketEntitledCountries].sort();
    }
    const countries = [...new Set(accounts.map(a => a.country).filter(Boolean))];
    return countries.sort();
  }, [accounts, showMarketExperience, marketEntitledCountries, userFilters.countryOptions]);

  const showSkeletons = isLoading && !hydratedFromCache;
  
  const tenantIdForMarket = effectiveTenant.toLowerCase();
  
  // UPDATED: Map calmedica, skillwell, kairadigital, wayground, and plataformaa tenants to their market identifiers
  let activeMarket;
  if (tenantIdForMarket === 'calmedica') {
    activeMarket = 'calmedica';
  } else if (tenantIdForMarket === 'learnwise') {
    activeMarket = 'learnwise';
  } else if (tenantIdForMarket === 'skillwell') {
    activeMarket = 'skillwell';
  } else if (tenantIdForMarket === 'kairadigital') {
    activeMarket = 'kairadigital';
  } else if (tenantIdForMarket === 'wayground') {
    activeMarket = 'wayground';
  } else   if (tenantIdForMarket === 'plataformaa') {
    activeMarket = 'plataformaa';
  } else if (tenantIdForMarket === 'demarque' || tenantIdForMarket === 'demarque_V2' || tenantIdForMarket === 'demarque_v2') {
    activeMarket = 'demarque';
  } else if (filters.country !== 'all') {
    activeMarket = COUNTRY_TO_MARKET[filters.country.toLowerCase()] || defaultMarketForTenant(tenantIdForMarket);
  } else if (tenantIdForMarket === 'smowl') {
    // For smowl tenant, default to South Africa market when no country filter is applied
    activeMarket = 'south-africa';
  } else if (tenantIdForMarket === 'diambra') {
    // Diambra uses same UK Market chart data as smowl; default to United Kingdom
    activeMarket = 'united-kingdom';
  } else {
    activeMarket = defaultMarketForTenant(tenantIdForMarket);
  }

  return (
    <div className="bg-gray-50 -m-4 md:-m-8 -mt-4 md:-mt-8 min-h-screen overflow-x-hidden">
      <div className="bg-white border-b mb-6 -mx-4 md:-mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-6 md:px-10 py-6 md:py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('marketIntelligence')}</h1>
              <p className="text-lg text-[#5C6B7A]">
                {showMarketExperience
                  ? t('mktPageSubtitle')
                  : t('macroTrends')}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* [2026-10-07] Only when there is a real choice. A tenant with ONE lens got a
                  dropdown holding one option, labelled "Product category" — which is what made it
                  look like a broken category filter rather than a lens picker. */}
              {showMarketExperience && marketLenses.length > 1 && (
                <Select value={marketLens || undefined} onValueChange={setMarketLens}>
                  <SelectTrigger className="w-[240px] border-gray-200">
                    <SelectValue placeholder={t('mktLens')} />
                  </SelectTrigger>
                  <SelectContent>
                    {marketLenses.map((l) => (
                      <SelectItem key={l.lens_key} value={l.lens_key}>
                        {lensDisplayName(
                          (l.lens_name || l.lens_key || ''),
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* THE REAL CATEGORY CONTROL. A lens can carry several categories (blackboard: lms,
                  course_evaluation, academic_integrity) and until now the header could only name
                  the lens, so the page showed one consolidated option and the intel never changed.
                  This drives the summary AND the opportunities table from one place — two controls
                  for one piece of state is how they drift. Shown only when the active lens has
                  more than one category; a single-category lens is unchanged. */}
              {showMarketExperience && activeLensCategories.length > 1 && (
                <Select value={marketCategory} onValueChange={setMarketCategory}>
                  <SelectTrigger className="w-[240px] border-gray-200">
                    <SelectValue placeholder={t('category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('mktAllCategories')}</SelectItem>
                    {activeLensCategories.map((c) => (
                      <SelectItem key={c} value={c}>{formatCategoryLabel(c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select
                value={countryFilter}
                onValueChange={(v) => (
                  showMarketExperience ? userFilters.setCountry(v) : handleFilterChange('country', v)
                )}
              >
                <SelectTrigger className="w-[240px] border-gray-200">
                  <SelectValue placeholder={t('filterByCountry')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {showMarketExperience ? t('mktAllEntitledCountries') : t('allCountries')}
                  </SelectItem>
                  {availableCountries.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full mb-6 h-auto min-h-9 ${
            marketTabCount >= 6 ? 'grid-cols-6'
              : marketTabCount === 5 ? 'grid-cols-5'
                : marketTabCount === 4 ? 'grid-cols-4'
                  : 'grid-cols-3'
          }`}>
            <TabsTrigger value="overview" onClick={() => setActiveTab('overview')}>{t('overview')}</TabsTrigger>
            {showBuyingActivityTab ? (
              <TabsTrigger value="buying" onClick={() => setActiveTab('buying')}>{t('buyingActivity')}</TabsTrigger>
            ) : null}
            <TabsTrigger value="players" onClick={() => setActiveTab('players')}>{t('players')}</TabsTrigger>
            {showCompetitorsTab ? (
              <TabsTrigger value="competitors" onClick={() => setActiveTab('competitors')}>{t('mktTabCompetitors')}</TabsTrigger>
            ) : null}
            {showMarketExperience ? (
              <TabsTrigger value="funding" onClick={() => setActiveTab('funding')}>{t('mktFunding')}</TabsTrigger>
            ) : null}
            {showMarketExperience ? (
              <TabsTrigger value="renewal" onClick={() => setActiveTab('renewal')}>{t('mktTabRenewalRisk')}</TabsTrigger>
            ) : null}
            {showMarketExperience ? (
              <TabsTrigger value="ignore" onClick={() => setActiveTab('ignore')}>{t('mktIgnore')}</TabsTrigger>
            ) : null}
          </TabsList>
        </Tabs>

          {activeTab === 'overview' ? (
          <div className="space-y-6 mt-2">
            {marketModulePending ? (
              <div className="space-y-4">
                <div className="h-16 bg-gray-100 rounded-md animate-pulse" />
                <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
                <div className="h-80 bg-gray-100 rounded-md animate-pulse" />
              </div>
            ) : showMarketExperience ? (
              <MarketExperience
                tenantId={effectiveTenant}
                lens={marketLens}
                lenses={marketLenses}
                category={marketCategory}
                canEdit={canEditMarket}
                countryFilter={countryFilter}
                lmsVendors={userFilters.lmsVendors}
                lmsFamilies={userFilters.lmsFamilies}
                onLmsVendorsChange={userFilters.setLmsVendors}
                filtersReady={userFilters.ready}
                onCountryChange={(country) => userFilters.setCountry(country)}
                moduleStatus={marketModuleStatus}
              />
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-12">
                {showSkeletons ? (
                  <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <MomentumTrend momentumScores={momentumScores} countryFilter={filters.country} market={activeMarket} />
                )}
              </div>

              <div className="lg:col-span-8">
                {showSkeletons ? (
                  <div className="h-80 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <DecisionWindowRadarFull accounts={filteredAccounts} isLoading={false} />
                )}
              </div>
              <div className="lg:col-span-4">
                {showSkeletons ? (
                  <div className="h-80 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <LiveDeals procurement={filteredProcurement} isLoading={false} currentTenant={effectiveTenant || currentTenant} />
                )}
              </div>

              <div className="lg:col-span-6">
                {showSkeletons ? (
                  <div className="h-72 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <TopAccounts accounts={filteredAccounts} isLoading={false} />
                )}
              </div>
              <div className="lg:col-span-6">
                {showSkeletons ? (
                  <div className="h-72 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <TopPersonas personas={filteredPersonas} isLoading={false} currentTenant={currentTenant} />
                )}
              </div>
              
              <div className="lg:col-span-6">
                {showSkeletons ? (
                  <div className="h-96 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <TieringSnapshot accounts={filteredAccounts} isLoading={false} />
                )}
              </div>
              
              <div className="lg:col-span-6">
                {showSkeletons ? (
                  <div className="h-96 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <AIReadinessIndex isLoading={false} market={activeMarket} />
                )}
              </div>
              
              <div className="lg:col-span-12">
                {showSkeletons ? (
                  <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <MarketFitGrid accounts={filteredAccounts} isLoading={false} />
                )}
              </div>
            </div>
            )}
          </div>
          ) : null}

          {showBuyingActivityTab && activeTab === 'buying' ? (
          <div className="space-y-6 mt-2">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-12">
                <TopBuyers market={activeMarket} />
              </div>

              <div className="lg:col-span-6">
                <KeyBuyingAreas market={activeMarket} />
              </div>

              <div className="lg:col-span-6">
                <ProcurementComplexity market={activeMarket} />
              </div>

              {activeMarket !== 'hong-kong' && activeMarket !== 'united-kingdom' && (
                <div className="lg:col-span-12">
                  <ProcurementRoutes market={activeMarket} />
                </div>
              )}
            </div>
          </div>
          ) : null}

          {activeTab === 'players' ? (
          <div className="space-y-6 mt-2">
            {marketModulePending ? (
              <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
            ) : showMarketExperience ? (
              <>
                <MarketSectorTrends tenantId={effectiveTenant} lens={marketLens} countryFilter={countryFilter} />
                <MarketPlayers tenantId={effectiveTenant} lens={marketLens} countryFilter={countryFilter} />
              </>
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-12">
                {showSkeletons ? (
                  <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
                ) : (
                  <MarketShare accounts={accountsForMarketShare} currentTenant={effectiveTenant || currentTenant} market={activeMarket} />
                )}
              </div>

              <div className="lg:col-span-12">
                <MarketWinners market={activeMarket} />
              </div>
            </div>
            )}
          </div>
          ) : null}

          {showCompetitorsTab && activeTab === 'competitors' ? (
            <div className="space-y-6 mt-2">
              <CompetitorsTab
                tenantId={effectiveTenant || currentTenant}
                countryFilter={countryFilter}
              />
            </div>
          ) : null}

          {showMarketExperience && activeTab === 'funding' ? (
          <div className="space-y-6 mt-2">
            {marketModulePending ? (
              <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
            ) : (
              <MarketFunding tenantId={effectiveTenant} lens={marketLens} countryFilter={countryFilter} />
            )}
          </div>
          ) : null}

          {showMarketExperience && activeTab === 'renewal' ? (
          <div className="space-y-6 mt-2">
            {marketModulePending ? (
              <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
            ) : (
              <MarketRenewalRisk tenantId={effectiveTenant} lens={marketLens} countryFilter={countryFilter} />
            )}
          </div>
          ) : null}

          {showMarketExperience && activeTab === 'ignore' ? (
          <div className="space-y-6 mt-2">
            {marketModulePending ? (
              <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
            ) : (
              <MarketIgnore tenantId={effectiveTenant} lens={marketLens} countryFilter={countryFilter} />
            )}
          </div>
          ) : null}
      </div>
    </div>
  );
}